import React, { useState } from 'react';
import { EntityNamespaceGroupsCard } from './EntityNamespaceGroupsCard';
import {
  Button,
  Grid,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  makeStyles,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { Navigate } from 'react-router-dom';
import {
  EntityApiDefinitionCard,
  EntityConsumingComponentsCard,
  EntityProvidingComponentsCard,
} from '@backstage/plugin-api-docs';
import {
  EntityAboutCard,
  EntityHasComponentsCard,
  EntityHasSystemsCard,
  EntityLayout,
  EntityLinksCard,
  EntitySwitch,
  EntityProcessingErrorsPanel,
  isComponentType,
  isKind,
  hasCatalogProcessingErrors,
  isOrphan,
  hasRelationWarnings,
} from '@backstage/plugin-catalog';
import {
  EntityUserProfileCard,
  EntityGroupProfileCard,
  EntityMembersListCard,
  EntityOwnershipCard,
} from '@backstage/plugin-org';
import { EmptyState } from '@backstage/core-components';
import { EntityCatalogGraphCard } from '@backstage/plugin-catalog-graph';

import { EntitySonarQubeCard } from '@backstage-community/plugin-sonarqube';

import {
  ComputeResourceDiscoveryContent,
  isComputeResourceDiscoveryAvailable,
} from '@internal/backstage-plugin-compute-resource-discovery';

import {
  EntityCloudbuildContent,
  isCloudbuildAvailable,
} from '@internal/backstage-plugin-cloudbuild';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  AdminerDatabaseComponent,
  isDatabaseMetadataAvailable,
} from '@internal/backstage-plugin-adminer-database';
import {
  isRedisMetadataAvailable,
  RedminerRedisComponent,
} from '@internal/backstage-plugin-redminer-redis';
import {
  EnvironmentVariablesComponent,
  isEnvironmentVariablesMetadataAvailable,
} from '@internal/backstage-plugin-environment-variables';
import {
  isAutomaticDeploymentMetadataAvailable,
  AutomaticDeploymentComponent,
} from '@internal/backstage-plugin-automatic-deployment';
import { LoggingPage } from '@internal/backstage-plugin-logging';

const SONARQUBE_MISSING_ANNOTATION_READ_MORE_URL =
  'https://github.com/backstage/community-plugins/blob/9f04c05e4f9e10b44bbf16fac9ad8e3a2eaae1ef/workspaces/sonarqube/plugins/sonarqube-backend/README.md#defaultsonarqubeinfoprovider';

const useDocsStyles = makeStyles(theme => ({
  formControl: {
    minWidth: 300,
    marginBottom: theme.spacing(3),
  },
  select: {
    backgroundColor: theme.palette.background.paper,
  },
}));

type DocSection =
  | 'database'
  | 'redis'
  | 'environmentVariables'
  | 'automaticDeployment';

const UnifiedDocsContent = () => {
  const classes = useDocsStyles();
  const [selectedDoc, setSelectedDoc] = useState<DocSection>('database');

  const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setSelectedDoc(event.target.value as DocSection);
  };

  const renderContent = () => {
    switch (selectedDoc) {
      case 'database':
        return adminerDatabaseContent;
      case 'redis':
        return redminerRedisContent;
      case 'environmentVariables':
        return environmentVariablesContent;
      case 'automaticDeployment':
        return automaticDeploymentContent;
      default:
        return null;
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <FormControl variant="outlined" className={classes.formControl}>
          <InputLabel id="doc-section-label">Documentation Section</InputLabel>
          <Select
            labelId="doc-section-label"
            id="doc-section-select"
            value={selectedDoc}
            onChange={handleChange}
            label="Documentation Section"
            className={classes.select}
          >
            <MenuItem value="database">Database</MenuItem>
            <MenuItem value="redis">Redis</MenuItem>
            <MenuItem value="environmentVariables">
              Environment Variables
            </MenuItem>
            <MenuItem value="automaticDeployment">
              Automatic Deployment
            </MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        {renderContent()}
      </Grid>
    </Grid>
  );
};

const cicdContent = (
  // This is an example of how you can implement your company's logic in entity page.
  // You can for example enforce that all components of type 'service' should use GitHubActions
  <EntitySwitch>
    {/*
      Here you can add support for different CI/CD services, for example
      using @backstage-community/plugin-github-actions as follows:
      <EntitySwitch.Case if={isGithubActionsAvailable}>
        <EntityGithubActionsContent />
      </EntitySwitch.Case>
     */}
    <EntitySwitch.Case if={isCloudbuildAvailable}>
      <EntityCloudbuildContent />
    </EntitySwitch.Case>
    <EntitySwitch.Case>
      <EmptyState
        title="No CI/CD available for this entity"
        missing="info"
        description="You need to add an annotation to your component if you want to enable CI/CD for it. You can read more about annotations in Backstage by clicking the button below."
        action={
          <Button
            variant="contained"
            color="primary"
            href="https://backstage.io/docs/features/software-catalog/well-known-annotations"
          >
            Read more
          </Button>
        }
      />
    </EntitySwitch.Case>
  </EntitySwitch>
);

const computeResourceContent = (
  <EntitySwitch>
    <EntitySwitch.Case if={isComputeResourceDiscoveryAvailable}>
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={12}>
          <ComputeResourceDiscoveryContent />
        </Grid>
      </Grid>
    </EntitySwitch.Case>
    <EntitySwitch.Case>
      <EmptyState
        title="No compute resources available for this entity."
        missing="info"
        description="You need to add computeResources metadata for this entity."
      />
    </EntitySwitch.Case>
  </EntitySwitch>
);

const adminerDatabaseContent = (
  <EntitySwitch>
    <EntitySwitch.Case if={isDatabaseMetadataAvailable}>
      <AdminerDatabaseComponent />
    </EntitySwitch.Case>
    <EntitySwitch.Case>
      <EmptyState
        title="No database available for this entity."
        missing="info"
        description="You need to add 'database' metadata for this entity."
      />
    </EntitySwitch.Case>
  </EntitySwitch>
);

const redminerRedisContent = (
  <EntitySwitch>
    <EntitySwitch.Case if={isRedisMetadataAvailable}>
      <RedminerRedisComponent />
    </EntitySwitch.Case>
    <EntitySwitch.Case>
      <EmptyState
        title="No redis available for this entity."
        missing="info"
        description="You need to add 'redis' metadata for this entity."
      />
    </EntitySwitch.Case>
  </EntitySwitch>
);

const environmentVariablesContent = (
  <EntitySwitch>
    <EntitySwitch.Case if={isEnvironmentVariablesMetadataAvailable}>
      <EnvironmentVariablesComponent />
    </EntitySwitch.Case>
    <EntitySwitch.Case>
      <EmptyState
        title="No environment variables available for this entity."
        missing="info"
        description="You need to add 'environmentVariables' metadata for this entity."
      />
    </EntitySwitch.Case>
  </EntitySwitch>
);

const automaticDeploymentContent = (
  <EntitySwitch>
    <EntitySwitch.Case if={isAutomaticDeploymentMetadataAvailable}>
      <AutomaticDeploymentComponent />
    </EntitySwitch.Case>
    <EntitySwitch.Case>
      <EmptyState
        title="No automatic deployment available for this entity."
        missing="info"
        description="You need to add 'automaticDeployment' metadata for this entity."
      />
    </EntitySwitch.Case>
  </EntitySwitch>
);

const loggingContent = (
  <Grid container spacing={3} alignItems="stretch">
    <Grid item md={12}>
      <LoggingPage />
    </Grid>
  </Grid>
);

const entityWarningContent = (
  <>
    <EntitySwitch>
      <EntitySwitch.Case if={isOrphan}>
        <Grid item xs={12}>
          <Alert severity="warning">
            This entity is not referenced by any location and is therefore not
            receiving updates. Unregister in the top right menu.
          </Alert>
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasRelationWarnings}>
        <Grid item xs={12}>
          <Alert severity="warning">
            This entity has relations to other entities, which can't be found in
            the catalog. Either the relations are broken, or you do not have the
            right permissions to see them.
          </Alert>
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasCatalogProcessingErrors}>
        <Grid item xs={12}>
          <EntityProcessingErrorsPanel />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
  </>
);

const overviewContent = (
  <Grid container spacing={3} alignItems="stretch">
    {entityWarningContent}
    <Grid item md={6}>
      <EntityAboutCard variant="gridItem" />
    </Grid>
    <Grid item md={6}>
      <EntitySonarQubeCard
        variant="gridItem"
        missingAnnotationReadMoreUrl={
          SONARQUBE_MISSING_ANNOTATION_READ_MORE_URL
        }
      />
    </Grid>
    <Grid item md={6} xs={12}>
      <EntityCatalogGraphCard variant="gridItem" height={400} />
    </Grid>

    <Grid item md={6} xs={12}>
      <EntityNamespaceGroupsCard />
      <Box mt={3}>
        <EntityLinksCard />
      </Box>
    </Grid>

    {/* <Grid item md={6}>
      <EntityGrafanaDashboardsCard />
    </Grid> */}
  </Grid>
);

const serviceEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/ci-cd" title="CI/CD">
      {cicdContent}
    </EntityLayout.Route>

    {/* <EntityLayout.Route
      path="/kubernetes"
      title="Kubernetes"
      if={isKubernetesAvailable}
    >
      <EntityKubernetesContent />
    </EntityLayout.Route> */}

    {/* <EntityLayout.Route path="/api" title="API">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityProvidedApisCard />
        </Grid>
        <Grid item md={6}>
          <EntityConsumedApisCard />
        </Grid>
      </Grid>
    </EntityLayout.Route> */}

    {/* <EntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityDependsOnComponentsCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityDependsOnResourcesCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route> */}
  </EntityLayout>
);

const websiteEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/ci-cd" title="CI/CD">
      {cicdContent}
    </EntityLayout.Route>

    {/* <EntityLayout.Route
      path="/kubernetes"
      title="Kubernetes"
      if={isKubernetesAvailable}
    >
      <EntityKubernetesContent />
    </EntityLayout.Route> */}

    {/* <EntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityDependsOnComponentsCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityDependsOnResourcesCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route> */}
  </EntityLayout>
);

/**
 * NOTE: This page is designed to work on small screens such as mobile devices.
 * This is based on Material UI Grid. If breakpoints are used, each grid item must set the `xs` prop to a column size or to `true`,
 * since this does not default. If no breakpoints are used, the items will equitably share the available space.
 * https://material-ui.com/components/grid/#basic-grid.
 */

const defaultEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>
  </EntityLayout>
);

const defaultComponentPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/ci-cd" title="CI/CD">
      {cicdContent}
    </EntityLayout.Route>
  </EntityLayout>
);

const DocumentationRedirect = () => {
  const { entity } = useEntity();
  const namespace = entity.metadata.namespace || 'default';
  const kind = entity.kind;
  const name = entity.metadata.name;

  const docsUrl = `/docs/${namespace}/${kind}/${name}`;

  return <Navigate to={docsUrl} replace />;
};

const documentationEntityPage = <DocumentationRedirect />;

const componentPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isComponentType('service')}>
      {serviceEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isComponentType('website')}>
      {websiteEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isComponentType('documentation')}>
      {documentationEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case>{defaultComponentPage}</EntitySwitch.Case>
  </EntitySwitch>
);

const apiPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard variant="gridItem" height={400} />
        </Grid>
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid container item md={12}>
          <Grid item md={6}>
            <EntityProvidingComponentsCard />
          </Grid>
          <Grid item md={6}>
            <EntityConsumingComponentsCard />
          </Grid>
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/definition" title="Definition">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityApiDefinitionCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const userPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityUserProfileCard variant="gridItem" />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const groupPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityGroupProfileCard variant="gridItem" />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard variant="gridItem" />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityMembersListCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityLinksCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const systemPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard variant="gridItem" />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard variant="gridItem" height={400} />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityNamespaceGroupsCard />
          <Box mt={3}>
            <EntityLinksCard />
          </Box>
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityHasComponentsCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/compute-resources" title="Compute Resources">
      {computeResourceContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      <UnifiedDocsContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/logs" title="Logging">
      {loggingContent}
    </EntityLayout.Route>
  </EntityLayout>
);

const domainPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard variant="gridItem" />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard variant="gridItem" height={400} />
        </Grid>
        <Grid item md={6}>
          <EntityHasSystemsCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

export const entityPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isKind('component')} children={componentPage} />
    <EntitySwitch.Case if={isKind('api')} children={apiPage} />
    <EntitySwitch.Case if={isKind('group')} children={groupPage} />
    <EntitySwitch.Case if={isKind('user')} children={userPage} />
    <EntitySwitch.Case if={isKind('system')} children={systemPage} />
    <EntitySwitch.Case if={isKind('domain')} children={domainPage} />

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);
